// Drawers de la tab {Tiendas} de Administración · {Tiendas y zonas}
// (handoff README §4, prototipo/sec-base.jsx SecActivos): «Editar datos»
// sobre PUT /assets/:id y «Nueva {tienda}» sobre POST /assets.
//
// El campo «Jefe de tienda» del drawer es el input de texto del
// prototipo: escribe `attributes[labels.assetInfoFieldKey]`
// («jefe_de_tienda»), la misma clave que llena la importación de planilla
// y que /cartera (PWA) muestra; PUT /assets/:id mezcla `attributes` sobre
// lo guardado, así que viaja sólo esa clave. El gerente (alcance org) no
// está sujeto al guard ADR-0051 de claves protegidas, y la PWA lo declara
// como el lugar donde se edita («via the dashboard, not by the supervisor
// on a visit»). Vincular una CUENTA de jefe (`storeManagerUserId`) sigue
// siendo cosa de la invitación: PUT no acepta ese campo.
//
// Lo que el handoff dibuja y el backend todavía no admite NO se muestra
// (regla «hide unusable actions»):
//   - Código: `assets.code` no existe (T-1iyh, pieza de migración).
//   - Marca en el drawer: PUT /assets/:id no acepta brandId; la marca de
//     una tienda nueva es la de su zona (ADR-0165 §5).
//   - Checklists al crear: POST /assets no los asigna.
//   - «Archivar {tienda}»: no hay archived_at ni ruta de archivo.
// Cada uno se reactiva cuando la ruta lo admita, sin tocar la tabla.

import { useState } from "react";
import type { AssetCreateBody, AssetUpdateBody } from "@bitacorapro/api-types";
import { Drawer } from "../../../components/Drawer";
import type { AdminAssetGroupRow, AdminAssetRow } from "./api";
import { Field, saveErrorMessage } from "./form";
import { useCreateAdminAsset, useUpdateAdminAsset } from "./queries";
import type { ActivosWords } from "./words";

type GroupOption = Pick<AdminAssetGroupRow, "id" | "name">;
type GroupId = NonNullable<AssetUpdateBody["assetGroupId"]>;

function byName(a: GroupOption, b: GroupOption): number {
  return a.name.localeCompare(b.name, "es");
}

// ── Editar datos ─────────────────────────────────────────────────────

export type EditAssetDrawerProps = {
  asset: AdminAssetRow;
  groups: readonly GroupOption[];
  /**
   * El jefe tal como lo muestra la tabla (atributo de la receta, o el
   * nombre de la cuenta vinculada si el atributo está vacío); null si no
   * hay ninguno. El campo parte de ahí y, si cambia, escribe el atributo.
   */
  leadName: string | null;
  words: ActivosWords;
  onClose: () => void;
  /** Guardado con éxito; el padre muestra el toast y cierra. */
  onSaved: (message: string) => void;
};

export function EditAssetDrawer({
  asset,
  groups,
  leadName,
  words: w,
  onClose,
  onSaved,
}: EditAssetDrawerProps) {
  const original = {
    unit: asset.unit ?? "",
    assetGroupId: asset.assetGroupId ?? "",
    address: asset.address ?? "",
    lead: leadName ?? "",
  };
  const [unit, setUnit] = useState(original.unit);
  const [assetGroupId, setAssetGroupId] = useState(original.assetGroupId);
  const [address, setAddress] = useState(original.address);
  const [lead, setLead] = useState(original.lead);
  const [errors, setErrors] = useState<{ unit?: string; group?: string; address?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const update = useUpdateAdminAsset();

  const leadEditable = w.assetLeadKey !== undefined;
  const dirty =
    unit.trim() !== original.unit ||
    assetGroupId !== original.assetGroupId ||
    address.trim() !== original.address ||
    (leadEditable && lead.trim() !== original.lead);
  const title = asset.unit ?? asset.address ?? w.assetOne;

  // Sólo viaja lo que cambió: PUT /assets/:id deja intacto lo que no
  // recibe. `address` y `unit` no admiten vacío en el wire (min 1), así
  // que vaciar un dato que existía se frena acá. El jefe sí puede
  // vaciarse: viaja como null y todos sus lectores lo tratan como ausente.
  function buildBody(): AssetUpdateBody | null {
    const next: { unit?: string; group?: string; address?: string } = {};
    const body: AssetUpdateBody = {};
    const name = unit.trim();
    if (name !== original.unit) {
      if (!name) next.unit = `Escribe el nombre de la ${w.assetOneLower}.`;
      else body.unit = name;
    }
    if (assetGroupId !== original.assetGroupId) {
      if (!assetGroupId && w.groupRequired) next.group = `Elige la ${w.groupOneLower}.`;
      // El id viene del <select> como string; el wire lo marca (brand).
      else body.assetGroupId = assetGroupId ? (assetGroupId as GroupId) : null;
    }
    const addr = address.trim();
    if (addr !== original.address) {
      if (!addr) next.address = "La dirección no puede quedar vacía.";
      else body.address = addr;
    }
    const jefe = lead.trim();
    if (leadEditable && jefe !== original.lead) {
      body.attributes = { [w.assetLeadKey as string]: jefe || null };
    }
    setErrors(next);
    return Object.keys(next).length === 0 ? body : null;
  }

  async function submit() {
    setFormError(null);
    const body = buildBody();
    if (!body) return;
    try {
      await update.mutateAsync({ id: asset.id, body });
      onSaved(`Datos de ${title} actualizados`);
    } catch (err) {
      setFormError(saveErrorMessage(err, w));
    }
  }

  return (
    <Drawer
      title={`Editar datos · ${title}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin guardar"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Guardar los datos de esta ${w.assetOneLower}`}
            disabled={!dirty || update.isPending}
            onClick={() => void submit()}
          >
            {update.isPending ? "Guardando…" : "Guardar"}
          </button>
        </>
      }
    >
      <p className="ag-muted ag-mb16">
        Cambia los datos de la {w.assetOneLower}. Las inspecciones y observaciones ya registradas no
        se modifican.
      </p>
      <Field label="Nombre" error={errors.unit ?? null}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={unit}
            maxLength={50}
            onChange={(e) => setUnit(e.target.value)}
          />
        )}
      </Field>
      <Field
        label={w.groupOne}
        hint={`Mover la ${w.assetOneLower} cambia quién la ve.`}
        error={errors.group ?? null}
      >
        {(ctl) => (
          <select
            {...ctl}
            className="select"
            value={assetGroupId}
            onChange={(e) => setAssetGroupId(e.target.value)}
          >
            {!w.groupRequired && <option value="">Sin {w.groupOneLower}</option>}
            {w.groupRequired && !original.assetGroupId && (
              <option value="">Elige una {w.groupOneLower}</option>
            )}
            {[...groups].sort(byName).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Dirección" error={errors.address ?? null}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={address}
            maxLength={500}
            placeholder="Calle, número, comuna"
            onChange={(e) => setAddress(e.target.value)}
          />
        )}
      </Field>
      {leadEditable && (
        <Field label={w.assetLead}>
          {(ctl) => (
            <input
              {...ctl}
              className="input"
              value={lead}
              maxLength={120}
              onChange={(e) => setLead(e.target.value)}
            />
          )}
        </Field>
      )}
      {formError && (
        <div className="auth-error" role="alert">
          {formError}
        </div>
      )}
    </Drawer>
  );
}

// ── Nueva {tienda} ───────────────────────────────────────────────────

export type NewAssetDrawerProps = {
  groups: readonly GroupOption[];
  words: ActivosWords;
  onClose: () => void;
  onSaved: (message: string) => void;
};

export function NewAssetDrawer({ groups, words: w, onClose, onSaved }: NewAssetDrawerProps) {
  const [unit, setUnit] = useState("");
  const [assetGroupId, setAssetGroupId] = useState("");
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState<{ unit?: string; group?: string; address?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateAdminAsset();

  const dirty = unit.trim() !== "" || assetGroupId !== "" || address.trim() !== "";

  function buildBody(): AssetCreateBody | null {
    const next: { unit?: string; group?: string; address?: string } = {};
    const name = unit.trim();
    const addr = address.trim();
    if (!name) next.unit = `Escribe el nombre de la ${w.assetOneLower}.`;
    if (!assetGroupId && w.groupRequired) next.group = `Elige la ${w.groupOneLower}.`;
    // POST /assets exige dirección (assetCreateSchema.address, min 1).
    if (!addr) next.address = "Escribe la dirección.";
    setErrors(next);
    if (Object.keys(next).length > 0) return null;
    return {
      unit: name,
      address: addr,
      attributes: {},
      ...(assetGroupId ? { assetGroupId } : {}),
    } as AssetCreateBody;
  }

  async function submit() {
    setFormError(null);
    const body = buildBody();
    if (!body) return;
    try {
      await create.mutateAsync(body);
      onSaved("Guardado");
    } catch (err) {
      setFormError(saveErrorMessage(err, w));
    }
  }

  return (
    <Drawer
      title={w.assetNew}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin crear"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Crear la ${w.assetOneLower} con estos datos`}
            disabled={!dirty || create.isPending}
            onClick={() => void submit()}
          >
            {create.isPending ? "Creando…" : "Crear"}
          </button>
        </>
      }
    >
      <Field label="Nombre" error={errors.unit ?? null}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={unit}
            maxLength={50}
            onChange={(e) => setUnit(e.target.value)}
          />
        )}
      </Field>
      <Field label={w.groupOne} error={errors.group ?? null}>
        {(ctl) => (
          <select
            {...ctl}
            className="select"
            value={assetGroupId}
            onChange={(e) => setAssetGroupId(e.target.value)}
          >
            <option value="">
              {w.groupRequired ? `Elige una ${w.groupOneLower}` : `Sin ${w.groupOneLower}`}
            </option>
            {[...groups].sort(byName).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Dirección" error={errors.address ?? null}>
        {(ctl) => (
          <input
            {...ctl}
            className="input"
            value={address}
            maxLength={500}
            placeholder="Calle, número, comuna"
            onChange={(e) => setAddress(e.target.value)}
          />
        )}
      </Field>
      {formError && (
        <div className="auth-error" role="alert">
          {formError}
        </div>
      )}
    </Drawer>
  );
}
